import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slide", "indicator", "container", "previousButton", "nextButton", "announcement"]
  static values = {
    autoplay: { type: Boolean, default: true },
    interval: { type: Number, default: 5000 },
    enableSwipe: { type: Boolean, default: true },
    pauseOnHover: { type: Boolean, default: true }
  }

  // Breakpoint thresholds for responsive slides
  static BREAKPOINTS = {
    MOBILE_MAX: 767,
    TABLET_MAX: 1023,
    DESKTOP_MIN: 1024
  }

  connect() {
    this.initializeCarousel()
    this.setupEventListeners()
    this.startAutoplay()
  }

  disconnect() {
    this.cleanup()
  }

  // Navigation actions - event handlers with descriptive names
  navigatePrevious(event) {
    event?.preventDefault()
    this.navigateToPreviousGroup()
  }

  navigateNext(event) {
    event?.preventDefault()
    this.navigateToNextGroup()
  }

  navigateToGroup(event) {
    event?.preventDefault()
    const groupIndex = parseInt(event.currentTarget.dataset.index, 10)
    this.goToGroup(groupIndex)
  }

  // Touch gesture handlers
  handleTouchStart(event) {
    if (!this.enableSwipeValue) return
    this.touchStartX = event.touches[0].clientX
    this.touchStartTime = Date.now()
  }

  handleTouchMove(event) {
    if (!this.enableSwipeValue || !this.touchStartX) return
    this.touchCurrentX = event.touches[0].clientX
  }

  handleTouchEnd(event) {
    if (!this.enableSwipeValue || !this.touchStartX) return

    const touchEndX = this.touchCurrentX || this.touchStartX
    const touchDuration = Date.now() - this.touchStartTime
    const swipeDistance = this.touchStartX - touchEndX
    const minimumSwipeDistance = 50
    const maximumSwipeDuration = 500

    if (Math.abs(swipeDistance) > minimumSwipeDistance && touchDuration < maximumSwipeDuration) {
      if (swipeDistance > 0) {
        this.navigateToNextGroup()
      } else {
        this.navigateToPreviousGroup()
      }
    }

    this.resetTouchState()
  }

  // Autoplay controls
  pauseAutoplay() {
    if (!this.pauseOnHoverValue) return
    this.stopAutoplay()
  }

  resumeAutoplay() {
    if (!this.pauseOnHoverValue) return
    this.startAutoplay()
  }

  // Private methods - initialization and setup
  initializeCarousel() {
    this.currentGroupIndex = 0
    this.slidesPerView = this.calculateSlidesPerView()
    this.updateCarouselPosition()
  }

  setupEventListeners() {
    this.boundHandleResize = this.handleResize.bind(this)
    this.boundHandleKeyboard = this.handleKeyboardNavigation.bind(this)

    window.addEventListener("resize", this.boundHandleResize)

    if (this.hasContainerTarget) {
      this.containerTarget.addEventListener("keydown", this.boundHandleKeyboard)
    }
  }

  cleanup() {
    this.stopAutoplay()

    if (this.boundHandleResize) {
      window.removeEventListener("resize", this.boundHandleResize)
    }

    if (this.hasContainerTarget && this.boundHandleKeyboard) {
      this.containerTarget.removeEventListener("keydown", this.boundHandleKeyboard)
    }
  }

  // Navigation logic
  navigateToPreviousGroup() {
    this.stopAutoplay()
    const maxGroupIndex = this.calculateMaxGroupIndex()
    this.currentGroupIndex = (this.currentGroupIndex - 1 + (maxGroupIndex + 1)) % (maxGroupIndex + 1)
    this.updateCarouselPosition()
    this.announceCurrentSlide()
    this.startAutoplay()
  }

  navigateToNextGroup() {
    this.stopAutoplay()
    const maxGroupIndex = this.calculateMaxGroupIndex()
    this.currentGroupIndex = (this.currentGroupIndex + 1) % (maxGroupIndex + 1)
    this.updateCarouselPosition()
    this.announceCurrentSlide()
    this.startAutoplay()
  }

  goToGroup(groupIndex) {
    this.stopAutoplay()
    this.currentGroupIndex = Math.max(0, Math.min(groupIndex, this.calculateMaxGroupIndex()))
    this.updateCarouselPosition()
    this.announceCurrentSlide()
    this.startAutoplay()
  }

  // Carousel update logic
  updateCarouselPosition() {
    this.slidesPerView = this.calculateSlidesPerView()
    const safeGroupIndex = this.getSafeGroupIndex()

    this.updateTrackPosition(safeGroupIndex)
    this.updateIndicators(safeGroupIndex)
    this.updateNavigationButtons(safeGroupIndex)
  }

  updateTrackPosition(groupIndex) {
    if (!this.hasSlideTarget || !this.hasContainerTarget) return

    const targetSlideIndex = groupIndex * this.slidesPerView
    const targetSlide = this.slideTargets[targetSlideIndex]

    if (targetSlide) {
      const slideLeft = targetSlide.offsetLeft
      this.containerTarget.scrollTo({
        left: slideLeft,
        behavior: 'smooth'
      })
    }
  }

  updateIndicators(activeGroupIndex) {
    if (!this.hasIndicatorTarget) return

    this.indicatorTargets.forEach((indicator, index) => {
      const isActive = index === activeGroupIndex

      indicator.setAttribute("aria-selected", isActive.toString())
      indicator.tabIndex = isActive ? 0 : -1

      if (isActive) {
        indicator.classList.remove("bg-gray-300", "w-2", "md:w-3")
        indicator.classList.add("bg-blue-600", "w-8", "md:w-10")
      } else {
        indicator.classList.remove("bg-blue-600", "w-8", "md:w-10")
        indicator.classList.add("bg-gray-300", "w-2", "md:w-3")
      }
    })
  }

  updateNavigationButtons(currentGroupIndex) {
    const maxGroupIndex = this.calculateMaxGroupIndex()

    if (this.hasPreviousButtonTarget) {
      this.previousButtonTarget.disabled = currentGroupIndex === 0
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = currentGroupIndex === maxGroupIndex
    }
  }

  // Accessibility - screen reader announcements
  announceCurrentSlide() {
    if (!this.hasAnnouncementTarget) return

    const currentSlide = this.currentGroupIndex + 1
    const totalGroups = this.calculateMaxGroupIndex() + 1
    const message = `Groupe ${currentSlide} sur ${totalGroups}`

    this.announcementTarget.textContent = message
  }

  // Keyboard navigation
  handleKeyboardNavigation(event) {
    const keyActions = {
      "ArrowLeft": () => this.navigateToPreviousGroup(),
      "ArrowRight": () => this.navigateToNextGroup(),
      "Home": () => this.goToGroup(0),
      "End": () => this.goToGroup(this.calculateMaxGroupIndex())
    }

    const action = keyActions[event.key]
    if (action) {
      event.preventDefault()
      action()
    }
  }

  // Responsive handling
  handleResize() {
    const newSlidesPerView = this.calculateSlidesPerView()

    if (newSlidesPerView !== this.slidesPerView) {
      this.slidesPerView = newSlidesPerView
      this.currentGroupIndex = this.recalculateGroupIndex()
      this.updateCarouselPosition()
    }
  }

  recalculateGroupIndex() {
    const maxGroupIndex = this.calculateMaxGroupIndex()
    return Math.min(this.currentGroupIndex, maxGroupIndex)
  }

  // Autoplay management
  startAutoplay() {
    if (!this.autoplayValue) return

    this.stopAutoplay()
    this.autoplayTimer = setInterval(() => {
      this.navigateToNextGroup()
    }, this.intervalValue)
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    }
  }

  // Calculation helpers
  calculateSlidesPerView() {
    const viewportWidth = window.innerWidth

    if (viewportWidth >= this.constructor.BREAKPOINTS.DESKTOP_MIN) {
      return 3
    } else if (viewportWidth >= this.constructor.BREAKPOINTS.MOBILE_MAX + 1) {
      return 2
    } else {
      return 1
    }
  }

  calculateMaxGroupIndex() {
    const totalSlides = this.slideTargets.length
    const totalGroups = Math.ceil(totalSlides / this.slidesPerView)
    return Math.max(0, totalGroups - 1)
  }

  getSafeGroupIndex() {
    const maxGroupIndex = this.calculateMaxGroupIndex()
    return Math.min(this.currentGroupIndex, maxGroupIndex)
  }

  // Touch state management
  resetTouchState() {
    this.touchStartX = null
    this.touchCurrentX = null
    this.touchStartTime = null
  }
}

